// src/FileUploader.ts
import createClient from "openapi-fetch";
import { FileUpload } from "./components/inputs/types";
import { API_ENDPOINT, MAX_POST_REQUEST_SIZE } from "./config";
import { paths } from "./api/types";

class FileUploader {
  constructor(
    private caseId: string,
    private revisionId: number,
    private idToken: string,
    private client: ReturnType<typeof createClient<paths>>,
    public sessionId?: string,
    private onProgress?: (file: File, progress: number) => void,
    private existingFiles: number[] = [],
    private newFiles: FileUpload[] = [],
    private maxPostRequestSize: number = MAX_POST_REQUEST_SIZE,
    /**
     * Hard cap on simultaneous HTTP requests spawned by uploadNew().
     * Tune to your backend/Cloud Run limits. 4–8 is usually safe.
     */
    private maxConcurrency: number = 3,
    /**
     * Optional per-file limit for chunked uploads (so one giant file
     * doesn’t hog all slots). If not set, falls back to maxConcurrency.
     */
    private perFileChunkConcurrency?: number
  ) {}

  private emitProgress(file: File, progress: number) {
    if (this.onProgress) {
      console.log(`Progress for ${file.name}: ${progress}%`);
      this.onProgress(file, progress);
    }
  }

  setProgress(onProgress: (file: File, progress: number) => void) {
    this.onProgress = onProgress;
  }

  async startSession(existingIds: number[], newFiles: FileUpload[]): Promise<void> {
    const newFilesData = newFiles.map(f => ({ filename: f.file.name, length: f.file.size }));

    const sessionResponse = await this.client.POST(
      '/v1/cases/{case_id}/revisions/{revision_id}/uploads/sessions',
      {
        params: {
            path:{ 
                case_id: this.caseId,
                revision_id: this.revisionId, 
            }
        },
        body: {
            existing_files: existingIds,
            new_files: newFilesData
        }
      }
     
    );

    if (!sessionResponse.response.ok) {
      const errorMsg = (sessionResponse.error as any)?.message;
      throw new Error(errorMsg ?? "Failed to start upload session");
    }

    const sessionId = sessionResponse.data?.session_id;
    if (!sessionId) {
      throw new Error("Upload session response missing session_id");
    }

    this.existingFiles = existingIds;
    this.newFiles = newFiles;
    this.sessionId = sessionId;
    this.maxPostRequestSize = sessionResponse.data?.max_post_size || MAX_POST_REQUEST_SIZE;
  }

  async copyExisting(): Promise<void> {
    if (!this.sessionId) throw new Error("Upload session not started");
    if (!this.existingFiles.length) return;

    const form = new FormData();
    form.append("payload", JSON.stringify({ file_ids: this.existingFiles }));

    const response = await fetch(
      `${API_ENDPOINT}/v1/cases/${this.caseId}/revisions/${this.revisionId}/uploads/copy`,
      {
        method: "POST",
        headers: { 
            Authorization: `Bearer ${this.idToken}`,  
            'X-Upload-Session-Id': this.sessionId 
        },
        body: form,
      }
    );

    if (!response.ok) {
      throw new Error("Failed to copy existing file references");
    }
  }

  /**
   * Run a set of async tasks with a concurrency limit.
   */
  private async runWithConcurrency<T>(
    taskFactories: Array<() => Promise<T>>,
    limit: number
  ): Promise<T[]> {
    const results: T[] = [];
    let i = 0;
    const workers = new Array(Math.min(limit, taskFactories.length)).fill(0).map(async () => {
      while (i < taskFactories.length) {
        const idx = i++;
        results[idx] = await taskFactories[idx]();
      }
    });
    await Promise.all(workers);
    return results;
  }

  async uploadNew(): Promise<void> {
    if (!this.sessionId) throw new Error("Upload session not started");
    if (!this.newFiles.length) return;

    // Sort files: small → large
    const sorted = [...this.newFiles].sort((a, b) => a.file.size - b.file.size);

    const taskFactories: Array<() => Promise<void>> = [];

    let batch: File[] = [];
    let batchSize = 0;

    const flushBatch = () => {
      if (!batch.length) return;
      const filesForThisBatch = batch;
      taskFactories.push(() => this.directUpload(filesForThisBatch));
      batch = [];
      batchSize = 0;
    };

    for (const f of sorted) {
      if (f.file.size > this.maxPostRequestSize) {
        // Flush any pending small-file batch first
        flushBatch();
        // Queue chunked upload as its own task
        const fileCopy = f;
        taskFactories.push(() => this.chunkedSingleConcurrent(fileCopy));
      } else {
        if (batchSize + f.file.size <= this.maxPostRequestSize) {
          batch.push(f.file);
          batchSize += f.file.size;
        } else {
          flushBatch();
          batch.push(f.file);
          batchSize = f.file.size;
        }
      }
    }
    // Remaining batch
    flushBatch();

    // Execute all uploads with a global concurrency cap
    await this.runWithConcurrency(taskFactories, this.maxConcurrency);
  }

  private async directUpload(files: File[]): Promise<void> {
    if (!this.sessionId) throw new Error("Upload session not started");

    const form = new FormData();
    files.forEach((file) => form.append("files", file, file.name));

    const res = await fetch(
      `${API_ENDPOINT}/v1/cases/${this.caseId}/revisions/${this.revisionId}/uploads/`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${this.idToken}`, 'X-Upload-Session-Id': this.sessionId },
        body: form,
      }
    );
    if (!res.ok) {
        const body = await res.json();
        if (body?.filename) {
          throw new Error(`${body?.filename}: ${body?.message}`);
        }
        if (body?.message) {
          throw new Error(`The file(s) failed to upload: ${body?.message}`);
        }
        throw new Error(`The file(s) failed to upload`);
    }

    files.forEach((file) => this.emitProgress(file, 100));
  }

  /**
   * Upload a large file in chunks, with per-file chunk concurrency.
   * Progress is emitted as chunks complete.
   */
  private async chunkedSingleConcurrent(file: FileUpload): Promise<void> {
    if (!this.sessionId) throw new Error("Upload session not started");
    if (this.idToken.length === 0) throw new Error("ID token is required for upload");

    const totalChunks = Math.ceil(file.file.size / this.maxPostRequestSize);
    let completedChunks = 0;

    const chunkFactories: Array<() => Promise<void>> = [];
    for (let i = 0; i < totalChunks; i++) {
      const start = i * this.maxPostRequestSize;
      const end = Math.min(start + this.maxPostRequestSize, file.file.size);
      const slice = file.file.slice(start, end);

      chunkFactories.push(async () => {
        const form = new FormData();
        form.append("file", slice, file.file.name);
        form.append(
          "chunk",
          JSON.stringify({
            chunk_index: i,
            total_chunks: totalChunks,
            filename: file.file.name
          })
        );

        const response = await fetch(
          `${API_ENDPOINT}/v1/cases/${this.caseId}/revisions/${this.revisionId}/uploads/chunk`,
          {
            method: "POST",
            headers: { 
              Authorization: `Bearer ${this.idToken}`, 
              "X-Upload-Session-Id": this.sessionId ?? "" 
            },
            body: form
          }
        );

        if (!response.ok) {
          throw new Error(`Chunk ${i + 1}/${totalChunks} failed for ${file.file.name}`);
        }

        try {
          const chunkJson = await response.json();
          console.log(`Chunk ${i + 1}/${totalChunks} uploaded:`, chunkJson);
        } catch {
          // Some backends respond with empty body. Ignore parse errors.
        }

        // Update progress as chunks finish (not strictly linear bytes, but close)
        completedChunks += 1;
        const progress = Math.round((completedChunks / totalChunks) * 100);
        this.emitProgress(file.file, progress);
      });
    }

    const perFileLimit = this.perFileChunkConcurrency ?? Math.min(4, this.maxConcurrency);
    await this.runWithConcurrency(chunkFactories, perFileLimit);
  }
}

export default FileUploader;
